import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import {
  ChevronLeft,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  User,
  Loader2,
  MoreHorizontal,
  UserPlus,
  UserCheck,
} from 'lucide-react';
import {
  usePost,
  useLikePost,
  useBookmarkPost,
} from '@/hooks/usePost';
import { useFollowUser } from '@/hooks/useUser';
import { useAuth } from '@/providers/AuthProvider';
import { formatCount, timeAgo } from '@/lib/formatters';
import { getPostContent } from '@/lib/postUtils';
import VideoPlayer from '@/components/VideoPlayer';
import CommentSection from '@/components/CommentSection';
import { getPostUser, getPostImages, getLikesCount } from '@/components/PostCard';
import type { Post } from '@/types';


function getVideoSource(post: Post): string | undefined {
  if (post.videoUrls?.hls) return post.videoUrls.hls;
  if (post.videoUrls?.mp4) return post.videoUrls.mp4;
  const videos = (post as Record<string, unknown>).videos as Array<Record<string, unknown>> | undefined;
  if (videos && videos.length > 0) {
    return (videos[0]?.url as string) || (videos[0]?.hls as string) || undefined;
  }
  return undefined;
}

function isVideoPost(post: Post): boolean {
  const type = (post.type as string | undefined) ?? '';
  return ['VIDEO', 'SHORT', 'BURST'].includes(type.toUpperCase());
}

/* ─── Share helper ─── */
async function copyUrl(url: string): Promise<boolean> {
  // Only use native share on HTTPS (mobile browsers) — it fails silently on localhost
  if (navigator.share && window.location.protocol === 'https:') {
    try { await navigator.share({ url }); return true; } catch { /* fall through */ }
  }
  // Clipboard API (works on localhost in modern browsers when page is focused)
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch { /* fall through */ }
  }
  // execCommand fallback — works everywhere
  try {
    const el = document.createElement('textarea');
    el.value = url;
    el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
    document.body.appendChild(el);
    el.focus();
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    if (ok) return true;
  } catch { /* fall through */ }
  return false;
}

function getCommentsCount(post: Post): number {
  return post.commentCount ?? post.postEngagement?.commentCount ?? 0;
}

/* ─── Main component ─── */

interface PostDetailProps {
  onBack?: () => void;
}

export default function PostDetail({ onBack }: PostDetailProps) {
  const { ulid } = useParams<{ ulid: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const currentUsername = currentUser?.username;

  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [copied, setCopied] = useState(false);
  const [followedUsers, setFollowedUsers] = useState<Set<string>>(new Set());
  const [imageIndex, setImageIndex] = useState(0);

  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const commentSectionRef = useRef<HTMLDivElement>(null);

  const { data: post, isLoading: postLoading } = usePost(ulid);

  const likeMutation = useLikePost();
  const bookmarkMutation = useBookmarkPost();
  const followMutation = useFollowUser();

  // Sync liked/bookmarked from post engagement
  useEffect(() => {
    if (!post) return;
    const eng = post.postEngagement;
    if (eng?.liked !== undefined) setLiked(Boolean(eng.liked));
    const bookmarkedValue = eng?.bookmarked ?? (post as Record<string, unknown>).isBookmarked;
    if (bookmarkedValue !== undefined) setBookmarked(Boolean(bookmarkedValue));
  }, [post]);

  // Entry animations
  useEffect(() => {
    gsap.fromTo(headerRef.current, { y: -20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.35, ease: 'power2.out' });
    gsap.fromTo(contentRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, delay: 0.1, ease: 'power2.out' });
  }, []);

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  const handleLike = () => {
    if (!ulid) return;
    setLiked((v) => !v);
    likeMutation.mutate({ ulid, liked });
  };

  const handleBookmark = () => {
    if (!ulid) return;
    const currentBookmarked = bookmarked;

    // Optimistic update
    setBookmarked(!currentBookmarked);

    bookmarkMutation.mutate(
      { ulid, bookmarked: currentBookmarked },
      {
        onError: () => {
          // Revert on error
          setBookmarked(currentBookmarked);
        },
      }
    );
  };

  const handleFollow = (username: string) => {
    const following = followedUsers.has(username);
    setFollowedUsers((prev) => {
      const next = new Set(prev);
      following ? next.delete(username) : next.add(username);
      return next;
    });
    followMutation.mutate({ username, following });
  };

  const scrollToComments = () => {
    commentSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleShare = async () => {
    if (!post) return;
    const url = `${window.location.origin}/post/${(post as Record<string, unknown>).ulid as string || ulid}`;
    await copyUrl(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /* ── Loading ── */
  if (postLoading) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex items-center justify-center">
        <Loader2 size={36} className="animate-spin text-[#A38BFF]" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex flex-col items-center justify-center gap-4">
        <p className="text-[#A7B0C8]">Post not found.</p>
        <button onClick={handleBack} className="text-[#A38BFF] text-sm">Go back</button>
      </div>
    );
  }

  const author = getPostUser(post);
  const content = getPostContent(post);
  const images = getPostImages(post);
  const videoSrc = getVideoSource(post);
  const showVideo = isVideoPost(post) && Boolean(videoSrc);
  const likesCount = getLikesCount(post);
  const commentsCount = getCommentsCount(post);
  const time = post.createdAt ? timeAgo(post.createdAt) : '';
  const isOwnPost = currentUsername && author.username === currentUsername;
  const isFollowing = followedUsers.has(author.username);

  return (
    <div className="min-h-screen bg-[#0B0D10] pb-32 flex flex-col">
      {/* Header */}
      <div
        ref={headerRef}
        className="sticky top-0 z-40 bg-[#0B0D10]/95 backdrop-blur-xl border-b border-white/[0.06] px-4 py-3 pt-safe flex items-center gap-3"
      >
        <button
          onClick={handleBack}
          className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8] flex-shrink-0"
        >
          <ChevronLeft size={22} />
        </button>
        <h1 className="text-base font-semibold text-[#F5F7FF]" style={{ fontFamily: 'Space Grotesk' }}>
          Post
        </h1>
        <div className="ml-auto">
          <button className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]">
            <MoreHorizontal size={20} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div ref={contentRef} className="px-4 pt-4">
        {/* Author row */}
        <div className="flex items-start gap-3 mb-4">
          <div onClick={() => author.username && navigate(`/profile/${author.username}`)} className="cursor-pointer">
            {author.avatar ? (
              <img src={author.avatar} alt={author.name} className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-11 h-11 rounded-full bg-[#141821] flex items-center justify-center flex-shrink-0">
                <User size={20} className="text-[#A7B0C8]" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p onClick={() => author.username && navigate(`/profile/${author.username}`)} className="text-sm font-semibold text-[#F5F7FF] cursor-pointer hover:underline">{author.name}</p>
            {author.username && (
              <p onClick={() => navigate(`/profile/${author.username}`)} className="text-xs text-[#A7B0C8] cursor-pointer hover:underline">@{author.username}</p>
            )}
            <p className="text-xs text-[#A7B0C8] mt-0.5">{time}</p>
          </div>
          {!isOwnPost && author.username && (
            <button
              onClick={() => handleFollow(author.username)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                isFollowing
                  ? 'bg-[#141821] text-[#A7B0C8] border border-white/[0.08]'
                  : 'bg-[#A38BFF] text-[#0B0D10]'
              }`}
            >
              {isFollowing ? <UserCheck size={13} /> : <UserPlus size={13} />}
              {isFollowing ? 'Following' : 'Follow'}
            </button>
          )}
        </div>

        {/* Post body */}
        {content ? (
          <p className="text-[#F5F7FF] text-base leading-relaxed mb-4 whitespace-pre-line">{content}</p>
        ) : null}

        {/* Video */}
        {showVideo && videoSrc && (
          <div className="rounded-xl overflow-hidden mb-4 aspect-video bg-black">
            <VideoPlayer src={videoSrc} autoPlay={false} className="w-full h-full" />
          </div>
        )}

        {/* Images */}
        {!showVideo && images.length > 0 && (
          <div className="mb-4">
            <div className="relative rounded-xl overflow-hidden">
              <img
                src={images[imageIndex]}
                alt="Post"
                className="w-full max-h-[28rem] object-contain"
              />
              {images.length > 1 && (
                <>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setImageIndex(i)}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${i === imageIndex ? 'bg-white' : 'bg-white/40'}`}
                      />
                    ))}
                  </div>
                  {imageIndex > 0 && (
                    <button
                      onClick={() => setImageIndex((v) => v - 1)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white"
                    >
                      ‹
                    </button>
                  )}
                  {imageIndex < images.length - 1 && (
                    <button
                      onClick={() => setImageIndex((v) => v + 1)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white"
                    >
                      ›
                    </button>
                  )}
                </>
              )}
            </div>
            {images.length > 1 && (
              <p className="text-xs text-[#A7B0C8] text-center mt-1">{imageIndex + 1} / {images.length}</p>
            )}
          </div>
        )}

        {/* Engagement bar */}
        <div className="flex items-center gap-5 py-3 border-t border-b border-white/[0.06] mb-4">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1.5 transition-colors ${liked ? 'text-[#FF4D4D]' : 'text-[#A7B0C8]'}`}
          >
            <Heart size={22} fill={liked ? '#FF4D4D' : 'none'} />
            <span className="text-sm font-medium">{formatCount(likesCount)}</span>
          </button>

          <button
            onClick={scrollToComments}
            className="flex items-center gap-1.5 text-[#A7B0C8]"
          >
            <MessageCircle size={22} />
            <span className="text-sm font-medium">{formatCount(commentsCount)}</span>
          </button>

          <button onClick={handleShare} className={`flex items-center gap-1.5 transition-colors ${copied ? 'text-[#1DB954]' : 'text-[#A7B0C8]'}`}>
            <Share2 size={22} />
            {copied && <span className="text-xs font-medium">Copied!</span>}
          </button>

          <button
            onClick={handleBookmark}
            className={`ml-auto transition-colors ${bookmarked ? 'text-[#A38BFF]' : 'text-[#A7B0C8]'}`}
          >
            <Bookmark size={22} fill={bookmarked ? '#A38BFF' : 'none'} />
          </button>
        </div>

        {/* Comments section */}
        <div ref={commentSectionRef}>
          {ulid && (
            <CommentSection
              postUlid={ulid}
              commentsCount={commentsCount}
              onProfileClick={(username) => navigate(`/profile/${username}`)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
